import React, { useCallback, useEffect, useState } from 'react';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import {
  Landmark, Sparkles, Loader2, X, ChevronRight,
  UserCheck, Plane, PlaneTakeoff, LogOut, BookMarked, ArrowRightLeft,
  Car, IdCard, FileText, Building2, Construction, Anchor, Receipt,
  Coins, Shield, Briefcase, Users, Store, Building, Star,
} from 'lucide-react';

/**
 * Government Services Workspace — specialised UI sitting on top of the
 * unified Bills engine. Each tile is a pre-wired template; clicking it
 * opens a one-field amount dialog and spawns a normal supplier_bill
 * via /api/government-services/{id}/quick-bill — Smart Mapping, VAT
 * Claim Lifecycle, audit trail and the Bills Center listing all fire
 * automatically thanks to the shared engine.
 */
const ICON_MAP = {
  UserCheck, Plane, PlaneTakeoff, LogOut, BookMarked, ArrowRightLeft,
  Car, IdCard, FileText, Building2, Construction, Anchor, Receipt,
  Coins, Shield, Briefcase, Users, Store, Building, Star,
};

const AGENCY_META = {
  absher:       { label: 'أَبشِر — وِزارَة الداخِليَّة',     short: 'أَبشِر',       tone: 'emerald' },
  muroor:       { label: 'المُرور / ساهِر',                   short: 'المُرور',     tone: 'red' },
  municipality: { label: 'البَلَدِيَّة',                        short: 'بَلَدِيَّة',   tone: 'teal' },
  customs:      { label: 'الجَمارِك',                          short: 'جَمارِك',     tone: 'blue' },
  zatca:        { label: 'هَيئة الزكاة والضَّريبة',             short: 'ZATCA',      tone: 'purple' },
  gosi:         { label: 'التَّأمينات الاجتِماعية',             short: 'GOSI',       tone: 'emerald' },
  mol:          { label: 'وزارَة المَوارِد البَشَرية',          short: 'العَمَل',    tone: 'emerald' },
  moc:          { label: 'وزارَة التِّجارَة',                  short: 'تِجارَة',     tone: 'indigo' },
  momrah:       { label: 'وزارَة الحَج والعُمرَة',              short: 'حَج وعُمرَة',tone: 'amber' },
  other:        { label: 'جِهَات أُخرى',                        short: 'أُخرى',      tone: 'slate' },
};

const TONE_CLS = {
  emerald: 'border-emerald-300 bg-emerald-50/40 hover:bg-emerald-100 text-emerald-900',
  red:     'border-red-300 bg-red-50/40 hover:bg-red-100 text-red-900',
  teal:    'border-teal-300 bg-teal-50/40 hover:bg-teal-100 text-teal-900',
  blue:    'border-blue-300 bg-blue-50/40 hover:bg-blue-100 text-blue-900',
  purple:  'border-purple-300 bg-purple-50/40 hover:bg-purple-100 text-purple-900',
  indigo:  'border-indigo-300 bg-indigo-50/40 hover:bg-indigo-100 text-indigo-900',
  amber:   'border-amber-300 bg-amber-50/40 hover:bg-amber-100 text-amber-900',
  slate:   'border-slate-300 bg-slate-50/40 hover:bg-slate-100 text-slate-900',
};

export default function GovernmentServices({ user }) {
  const [grouped, setGrouped] = useState({});
  const [loading, setLoading] = useState(false);
  const [picking, setPicking] = useState(null);

  const isAdmin = user?.role === 'admin' || user?.role === 'pilot_admin';

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await axiosInstance.get('/government-services/by-agency');
      setGrouped(data.agencies || {});
    } catch (e) {
      toast.error(e.response?.data?.detail || 'تَعَذَّر التَّحميل');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const seed = async () => {
    if (!isAdmin) return;
    try {
      const { data } = await axiosInstance.post('/government-services/seed');
      toast.success(`تَم تَركيب ${data.installed} خِدمَة حُكومية (${data.skipped} مَوجود سَلَفاً)`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'فَشِل التَّركيب');
    }
  };

  const totalCount = Object.values(grouped).reduce((s, l) => s + l.length, 0);

  return (
    <div className="p-6 space-y-5" data-testid="government-services-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Landmark className="w-7 h-7 text-indigo-600" />
            مَركَز الخِدمات الحُكومية
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            خِدمات أَبشِر، المُرور، البَلَدِيَّة، الجَمارِك، ZATCA، GOSI، التِّجارَة وأَكثَر — كُلُّها بِنَقرَة واحِدَة فَوق مُحَرِّك الفَواتير المُوَحَّد.
          </p>
        </div>
        {isAdmin && totalCount === 0 && (
          <button
            onClick={seed}
            data-testid="gs-seed-btn"
            className="px-3 py-1.5 rounded-lg text-xs bg-indigo-50 text-indigo-700 border border-indigo-200 hover:bg-indigo-100 inline-flex items-center gap-1"
          >
            <Sparkles className="w-3.5 h-3.5" /> تَركيب الباقَة الافتِراضية
          </button>
        )}
      </div>

      {loading ? (
        <div className="bg-white rounded-xl border border-slate-200 p-12 text-center text-slate-500">
          <Loader2 className="w-6 h-6 animate-spin inline" />
        </div>
      ) : totalCount === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 p-12 text-center text-slate-500">
          <Landmark className="w-12 h-12 mx-auto mb-3 opacity-30" />
          لا تُوجَد خَدَمات بَعد.
          {isAdmin && (
            <div className="mt-3">
              <button
                onClick={seed}
                className="px-4 py-2 rounded-lg bg-gradient-to-r from-indigo-600 to-purple-600 text-white text-sm font-semibold"
              >
                <Sparkles className="w-4 h-4 inline me-1" /> تَركيب الباقَة الآن
              </button>
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-5">
          {Object.entries(AGENCY_META).map(([key, meta]) => {
            const items = grouped[key] || [];
            if (items.length === 0) return null;
            return (
              <section key={key} data-testid={`gs-agency-${key}`}>
                <div className="flex items-center gap-2 mb-2">
                  <span className={`w-2 h-2 rounded-full bg-${meta.tone}-500`} />
                  <h2 className="text-sm font-bold text-slate-800">{meta.label}</h2>
                  <span className="text-xs text-slate-500">· {items.length} خِدمَة</span>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-2">
                  {items.map((tpl) => {
                    const Icon = ICON_MAP[tpl.icon] || FileText;
                    return (
                      <button
                        key={tpl.id}
                        onClick={() => setPicking(tpl)}
                        data-testid={`gs-tile-${tpl.code}`}
                        className={`text-start rounded-xl border p-3 transition-all ${TONE_CLS[tpl.color] || TONE_CLS.slate}`}
                      >
                        <div className="flex items-center justify-between">
                          <Icon className="w-5 h-5" />
                          <ChevronRight className="w-4 h-4 opacity-50" />
                        </div>
                        <div className="mt-2 font-semibold text-sm">{tpl.name_ar}</div>
                        <div className="text-[10px] opacity-70 mt-0.5">{tpl.name_en}</div>
                        {tpl.description && (
                          <div className="text-[10px] mt-1 line-clamp-2 opacity-70">{tpl.description}</div>
                        )}
                      </button>
                    );
                  })}
                </div>
              </section>
            );
          })}
        </div>
      )}

      {picking && (
        <QuickBillModal
          template={picking}
          onClose={() => setPicking(null)}
          onCreated={(bill) => {
            setPicking(null);
            toast.success(`تَم إنشاء الفاتورة ${bill.bill_number}`);
          }}
        />
      )}
    </div>
  );
}


// ─── Quick Bill Modal ────────────────────────────────────────────


const QuickBillModal = ({ template, onClose, onCreated }) => {
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [referenceNo, setReferenceNo] = useState('');
  const [sadadBillNo, setSadadBillNo] = useState('');
  const [billDate, setBillDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) {
      toast.error('أَدخِل مَبلَغاً صَحيحاً');
      return;
    }
    try {
      setSubmitting(true);
      const { data } = await axiosInstance.post(
        `/government-services/${template.id}/quick-bill`,
        {
          amount: amt,
          description: description || template.name_ar,
          bill_date: billDate,
          reference_no: referenceNo || null,
          sadad_bill_no: sadadBillNo || null,
        },
      );
      onCreated?.(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'فَشِل إنشاء الفاتورة');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="gs-quick-bill-modal" role="dialog" aria-modal="true">
      <form onSubmit={submit} className="bg-white rounded-xl shadow-2xl max-w-md w-full overflow-hidden">
        <div className="p-5 bg-gradient-to-r from-indigo-600 to-purple-600 text-white flex items-start justify-between">
          <div>
            <h3 className="font-bold text-lg">{template.name_ar}</h3>
            <p className="text-xs opacity-90 mt-0.5">{template.name_en}</p>
          </div>
          <button type="button" onClick={onClose} className="hover:bg-white/20 rounded-lg p-1.5" data-testid="gs-quick-close">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-5 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-xs font-semibold text-slate-700">المَبلَغ (شامِل VAT) *</span>
              <input
                type="number" step="0.01" value={amount}
                onChange={(e) => setAmount(e.target.value)}
                data-testid="gs-quick-amount"
                placeholder="0.00"
                autoFocus
                className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-lg font-mono"
              />
            </label>
            <label className="block">
              <span className="text-xs font-semibold text-slate-700">تاريخ الفاتورة</span>
              <input
                type="date" value={billDate}
                onChange={(e) => setBillDate(e.target.value)}
                data-testid="gs-quick-date"
                className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
              />
            </label>
          </div>
          <label className="block">
            <span className="text-xs font-semibold text-slate-700">الوَصف (اختياري)</span>
            <input
              type="text" value={description}
              onChange={(e) => setDescription(e.target.value)}
              data-testid="gs-quick-desc"
              placeholder={template.name_ar}
              className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
            />
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-xs font-semibold text-slate-700">رَقم سَداد (اختياري)</span>
              <input
                type="text" value={sadadBillNo}
                onChange={(e) => setSadadBillNo(e.target.value)}
                data-testid="gs-quick-sadad"
                className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm font-mono"
              />
            </label>
            <label className="block">
              <span className="text-xs font-semibold text-slate-700">المَرجِع</span>
              <input
                type="text" value={referenceNo}
                onChange={(e) => setReferenceNo(e.target.value)}
                data-testid="gs-quick-ref"
                className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm font-mono"
              />
            </label>
          </div>
          <div className="rounded-lg bg-slate-50 border border-slate-200 p-2.5 text-[11px] text-slate-600">
            <strong>VAT %:</strong> {template.default_vat_rate} ·{' '}
            <strong>التَّصنيف:</strong> {template.expense_category_code} ·{' '}
            <strong>مَوعِد الاستِحقاق:</strong> +{template.default_due_days} يَوم
          </div>
        </div>
        <div className="p-4 bg-slate-50 border-t flex items-center justify-end gap-2">
          <button type="button" onClick={onClose}
            className="px-4 py-2 rounded-lg text-sm bg-white border border-slate-300 text-slate-700 hover:bg-slate-100"
          >إِلغاء</button>
          <button type="submit" disabled={submitting}
            data-testid="gs-quick-submit"
            className="px-4 py-2 rounded-lg text-sm bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-semibold disabled:opacity-50"
          >{submitting ? '...جارٍ الإنشاء' : 'إنشاء الفاتورة'}</button>
        </div>
      </form>
    </div>
  );
};
